import React, { useCallback, useEffect, useRef } from 'react'
import { getRemotionEnvironment, Img } from 'remotion'
import type { Backdrop, VideoFit } from '../types/renderConfigV2'

/**
 * How a video fills its box (#547), and what fills the box around it.
 *
 * Auto (no stored `fit`) is what v1 did: a video whose aspect matches its box
 * fills it, and one that does not (a 16:9 recording in a portrait project)
 * is shown whole, letterboxed over a blurred, darkened copy of itself, so
 * no speaker is cropped out. An explicit pick is stored as `cover` (Fill) or
 * `contain` (Fit). The box is the item's full, uncropped box (`innerStyle`
 * sizes the picture to it and the crop then clips that); the backdrop fills
 * the outer, cropped rect.
 */

/** Aspects within 1 % are a match: a 1920×1080 video in a 1080×608 box fills it. */
export const FIT_TOLERANCE = 0.01

/** The fit in effect. A missing or zero size (a migrated asset) is `cover`. */
export function resolveFit(
  item: { fit?: VideoFit; width: number; height: number },
  asset: { width?: number; height?: number } | null | undefined,
): VideoFit {
  if (item.fit) return item.fit
  const w = asset?.width
  const h = asset?.height
  if (!w || !h || !item.width || !item.height) return 'cover'
  const ratio = w / h / (item.width / item.height)
  return Math.abs(ratio - 1) <= FIT_TOLERANCE ? 'cover' : 'contain'
}

/** Whether Auto would crop the picture: the aspects differ. */
export function aspectsDiffer(
  item: { width: number; height: number },
  asset: { width?: number; height?: number } | null | undefined,
): boolean {
  return (
    resolveFit({ width: item.width, height: item.height }, asset) !== 'cover'
  )
}

/** The colour behind a blur before its first frame or during a seek. */
export const BACKDROP_FILL = '#0b0b0b'

/** The blur canvas is this fraction of the box: cheap to draw and to blur. */
const BLUR_SCALE = 1 / 8
const BLUR_PX = 4

const fill: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
}

function sizeOf(source: CanvasImageSource): { width: number; height: number } {
  if (typeof VideoFrame !== 'undefined' && source instanceof VideoFrame)
    return { width: source.displayWidth, height: source.displayHeight }
  if (
    typeof HTMLVideoElement !== 'undefined' &&
    source instanceof HTMLVideoElement
  )
    return { width: source.videoWidth, height: source.videoHeight }
  const sized = source as {
    width: number | SVGAnimatedLength
    height: number | SVGAnimatedLength
  }
  const width =
    typeof sized.width === 'number' ? sized.width : sized.width.baseVal.value
  const height =
    typeof sized.height === 'number' ? sized.height : sized.height.baseVal.value
  return { width, height }
}

/**
 * A blurred backdrop drawn from the video's own decode: `onVideoFrame` hands
 * every frame to `draw`, which paints it cover-scaled, blurred and darkened
 * onto a small canvas behind the picture. Never a second `<Video>`. It draws
 * synchronously, because the render path closes the frame right after the
 * callback returns. Each cut piece owns its own (#77: pieces are premounted,
 * and a premounted piece paints the canvas hidden with it).
 */
export function useBlurBackdrop(
  box: { width: number; height: number },
  active: boolean,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const width = Math.max(1, Math.round(box.width * BLUR_SCALE))
  const height = Math.max(1, Math.round(box.height * BLUR_SCALE))
  const onVideoFrame = useCallback(
    (frame: CanvasImageSource) => {
      const canvas = canvasRef.current
      const ctx = canvas?.getContext('2d')
      if (!canvas || !ctx) return
      const source = sizeOf(frame)
      if (!source.width || !source.height) return
      // Overscan by the blur, so the edges blur into picture, not into nothing.
      const scale = Math.max(width / source.width, height / source.height)
      const w = source.width * scale + BLUR_PX * 4
      const h = source.height * scale + BLUR_PX * 4
      ctx.filter = 'none'
      ctx.fillStyle = BACKDROP_FILL
      ctx.fillRect(0, 0, width, height)
      ctx.filter = `blur(${BLUR_PX}px) brightness(0.6)`
      ctx.drawImage(frame, (width - w) / 2, (height - h) / 2, w, h)
    },
    [width, height],
  )
  // A backdrop that appears while the editor is paused (Fill → Fit) gets no
  // new frame until playback or a seek: paint it once from the frame the
  // picture already shows, on the media tag's own canvas beside this one. A
  // render needs none of this: its frames all arrive through `onVideoFrame`.
  useEffect(() => {
    if (!active || getRemotionEnvironment().isRendering) return
    const id = requestAnimationFrame(() => {
      const own = canvasRef.current
      const shown = [
        ...(own?.parentElement?.querySelectorAll('canvas, video') ?? []),
      ].find((el) => el !== own) as
        | HTMLCanvasElement
        | HTMLVideoElement
        | undefined
      if (shown && sizeOf(shown).width) onVideoFrame(shown)
    })
    return () => cancelAnimationFrame(id)
  }, [active, onVideoFrame])
  const canvas = (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ ...fill, backgroundColor: BACKDROP_FILL }}
    />
  )
  return { canvas, onVideoFrame }
}

/**
 * What fills a fitted picture's box, behind it: a colour, or for an image
 * (a template tile's still) the same picture again, covering, blurred and
 * darkened — a cached image is cheap to draw twice; a video is not, which is
 * why `VideoLayer` draws its blur from its own frames instead.
 */
export function ImageBackdrop({
  backdrop,
  src,
}: {
  backdrop: Backdrop | undefined
  src: string
}) {
  if (backdrop?.kind === 'color')
    return <div style={{ ...fill, backgroundColor: backdrop.color }} />
  return (
    <div
      style={{ ...fill, overflow: 'hidden', backgroundColor: BACKDROP_FILL }}
    >
      <Img
        alt=""
        crossOrigin="anonymous"
        pauseWhenLoading
        src={src}
        style={{
          ...fill,
          objectFit: 'cover',
          filter: 'blur(32px) brightness(0.6)',
          transform: 'scale(1.1)',
        }}
      />
    </div>
  )
}

/** A colour backdrop: a plain fill, no decode. */
export function ColorBackdrop({ color }: { color: string }) {
  return <div style={{ ...fill, backgroundColor: color }} />
}
